import { ColDef, RowClickedEvent } from "ag-grid-community";
import { AgGridReact } from "ag-grid-react";
import { useCallback, useEffect, useMemo, useState } from "react";
import { aggridHeaderClass } from "src/components/common/common";
import { useGridSettingContent } from "src/utils/grid/GridSettingContent";
import { defaultColDef, getDefaultRowIndexColumnConfig } from "src/utils/grid/gridStyleUtils";
import { GridContextWrapper } from "src/components/common/gridWrapper/GridContextWrapper";
import Grid_Touch from "src/components/common/gridWrapper/Grid_Touch";
import { useCommonCodeStore } from "src/store/useCommonCodeStore";
import { defaultProductResultData, PRD3031_R01GridRowData, PRD3031_R01_SUBTOTAL_TYPES } from "./Dto_PRD3031_R01";
import { createSubtotalGuards } from "src/utils/subtotal/guards";
import { usePRD3031_R01Store } from "./Store_PRD3031_R01";
import { formatDate } from "src/utils/string/stringUtil";
import { CommonCodeColumn, CompCdColumn } from "src/utils/grid/GridColumnContent";
import { calculateSubtotals, calculateDateSubtotals, mergeDataWithSubtotals } from "./utils/subtotalUtils";
import { formatNumber } from "src/utils/number/numberUtils";
import { GridCustomColumnDefs } from "src/utils/grid/GridCustomColumnDefs";
import { buildDefaultColumnStateFromDefs } from "src/utils/grid/columnPinUtils";
import { onlyTouchGridStyle } from "src/utils/grid/gridFunctionUtil";

const { isSubtotalRow, isItemSubtotalRow, isDateSubtotalRow } = createSubtotalGuards<PRD3031_R01GridRowData>({
    isSubtotalKey: "isSubtotal",
    typeKey: "subtotalType",
    itemTypeValue: PRD3031_R01_SUBTOTAL_TYPES.ITEM,
    dateTypeValue: PRD3031_R01_SUBTOTAL_TYPES.DATE,
});

const COLUMN_STATE_KEY = 'PRD3031_R01-grid-column-state';

interface Props {
    gridRef: React.RefObject<AgGridReact<PRD3031_R01GridRowData> | null>;
}

export const Table_PRD3031_R01 = (props: Props) => {
    const { gridRef } = props;

    const {
        PRD3031_R01List,
        selectedPRD3031_R01List,
        setSelectedPRD3031_R01List,
        initSelectedPRD3031_R01List,
        columnState,
        setColumnState,
        scrollTop, setScrollTop,
        showSubtotals,
        subtotalData,
        setSubtotalData,
        dateSubtotalData,
        setDateSubtotalData,
        gridRowData,
        setGridRowData,
    } = usePRD3031_R01Store();

    const {
        jobTeamList,
        ordTypeList,
        unitList,
        compCdList,
        employeeCdList,
        workPlaceList,
    } = useCommonCodeStore();

    const codeMaps = useMemo(
        () => ({
            ordType: new Map(ordTypeList.map((v) => [v.minorCd, v.codeNm])),
            workPlace: new Map(workPlaceList.map((v) => [v.workplaceCd, v.workplaceNm])),
            unit: new Map(unitList.map((v) => [v.minorCd, v.codeNm])),
            emp: new Map(employeeCdList.map((v) => [v.empNo, v.empNm])),
            comp: new Map(compCdList.map((v) => [v.compCd, v.compNm])),
        }),
        [ordTypeList, workPlaceList, unitList, employeeCdList, compCdList],
    );

    const [hoveredRowIndex, setHoveredRowIndex] = useState<number | null>(null);

    const {
        createRowClassRules,
        ensureSelectedRow,
        settingRowIndex,
    } = useGridSettingContent({
        gridRef,
        key: "productResultNo",
        gridData: gridRowData || [],
        defaultGridData: defaultProductResultData as PRD3031_R01GridRowData,
        selectedRow: selectedPRD3031_R01List,
    });

    // 소계 행 / 일반 행 분리
    const getRowIdForGrid = useCallback((params: any) => {
        if (isSubtotalRow(params.data)) {
            if (isItemSubtotalRow(params.data)) {
                return `${params.data.itemCd}_item_subtotal`;
            }
            if (isDateSubtotalRow(params.data)) {
                return `${params.data.itemCd}_${params.data.productDt}_date_subtotal`;
            }
            return `${params.data.itemCd}_subtotal`;
        }
        return params.data?.productResultNo || params.id;
    }, []);

    // 단일 행 선택 + 선택 상태 업데이트
    const handleRowClick = useCallback(
        (event: RowClickedEvent<PRD3031_R01GridRowData>) => {
            if (event.node.rowPinned === "bottom") return;
            const data = event.data;
            if (!data || isSubtotalRow(data)) return;

            const isSelected = selectedPRD3031_R01List.includes(data.productResultNo);
            if (isSelected) {
                setSelectedPRD3031_R01List([]);
            } else {
                setSelectedPRD3031_R01List([data.productResultNo]);
            }

        },
        [selectedPRD3031_R01List, setSelectedPRD3031_R01List],
    );

    // idKey: 'compCd' -> 'productResultNo'
    const getCellStyle = useCallback((params: any, key: string) => onlyTouchGridStyle(key, "productResultNo")(params), []);

    // 소계 데이터 계산 및 그리드 데이터 생성
    useEffect(() => {
        if (PRD3031_R01List && PRD3031_R01List.length > 0) {
            const subtotals = calculateSubtotals(PRD3031_R01List);
            setSubtotalData(subtotals);
            const dateSubtotals = calculateDateSubtotals(PRD3031_R01List);
            setDateSubtotalData(dateSubtotals);
            const mergedData = mergeDataWithSubtotals(
                PRD3031_R01List,
                subtotals,
                dateSubtotals,
                showSubtotals,
            );
            setGridRowData(mergedData);
        } else {
            setSubtotalData([]);
            setDateSubtotalData([]);
            setGridRowData([]);
        }
    }, [PRD3031_R01List, showSubtotals, setSubtotalData, setDateSubtotalData, setGridRowData]);

    // 소계 표시 상태 변경 시 재생성
    useEffect(() => {
        if (
            PRD3031_R01List &&
            PRD3031_R01List.length > 0 &&
            (subtotalData.length > 0 || dateSubtotalData.length > 0)
        ) {
            const mergedData = mergeDataWithSubtotals(
                PRD3031_R01List,
                subtotalData,
                dateSubtotalData,
                showSubtotals,
            );
            setGridRowData(mergedData);
        }
    }, [showSubtotals, PRD3031_R01List, subtotalData, dateSubtotalData, setGridRowData]);

    const headerNames: Record<string, string> = {
        rowIndex: "",
        productResultNo: "생산실적번호",
        compCd: "사업장",
        compNm: "회사명",
        productDt: "생산일자",
        orderType: "작업구분",
        orderTypeNm: "주문유형명",
        workplaceCd: "공정명",
        workplaceNm: "공정명",
        jobTeam: "작업팀",
        jobTeamNm: "작업팀명",
        itemCd: "품목코드",
        itemNm: "품명",
        itemSpec: "규격",
        itemUnit: "단위",
        orderQty: "지시량",
        productEmp: "작업자",
        empNm: "작업자명",
        productQty: "생산량",
        badQty: "불량",
        acceptQty: "실적량",
    };

    const baseColumnDefs: ColDef<PRD3031_R01GridRowData>[] = useMemo(
        () => [
            ...getDefaultRowIndexColumnConfig(),
            {
                headerName: "생산실적번호",
                colId: "productResultNo",
                field: "productResultNo" as keyof PRD3031_R01GridRowData,
                width: 160,
                editable: false,
                cellStyle: (params) => getCellStyle(params, "productResultNo"),
                cellRenderer: (params: any) => {
                    if (isSubtotalRow(params.data)) return "";
                    return params.data?.productResultNo || "";
                },
            },
            {
                headerName: "사업장",
                colId: "compCd",
                field: "compCd" as keyof PRD3031_R01GridRowData,
                width: 120,
                editable: false,
                cellStyle: (params) => getCellStyle(params, "compCd"),
                ...CompCdColumn.options(compCdList, false, undefined, undefined),
            },
            {
                headerName: "생산일자",
                colId: "productDt",
                field: "productDt" as keyof PRD3031_R01GridRowData,
                width: 120,
                editable: false,
                cellStyle: (params) => getCellStyle(params, "productDt"),
                cellRenderer: (params: any) => {
                    if (isSubtotalRow(params.data)) return "";
                    if (params.data?.productDt) {
                        const date = new Date(params.data.productDt);
                        if (!isNaN(date.getTime())) return formatDate(date);
                    }
                    return "";
                },
                valueFormatter: (params: any) => {
                    if (params.data?.productDt) {
                        const date = new Date(params.data.productDt);
                        if (!isNaN(date.getTime())) return formatDate(date);
                    }
                    return "";
                },
            },
            {
                headerName: "작업구분",
                colId: "orderType",
                field: "orderType" as keyof PRD3031_R01GridRowData,
                width: 75,
                editable: false,
                cellStyle: (params) => getCellStyle(params, "orderType"),
                cellRenderer: (params: any) => {
                    if (isSubtotalRow(params.data)) return "";
                    return codeMaps.ordType.get(params.data?.orderType) || "";
                },
                valueGetter: (params: any) => codeMaps.ordType.get(params.data?.orderType) || "",
            },
            {
                headerName: "공정명",
                colId: "workplaceCd",
                field: "workplaceCd" as keyof PRD3031_R01GridRowData,
                width: 120,
                editable: false,
                cellStyle: (params) => getCellStyle(params, "workplaceCd"),
                cellRenderer: (params: any) => {
                    if (isSubtotalRow(params.data)) return "";
                    return codeMaps.workPlace.get(params.data?.workplaceCd) || "";
                },
                valueGetter: (params: any) => codeMaps.workPlace.get(params.data?.workplaceCd) || "",
            },
            {
                headerName: "작업팀",
                colId: "jobTeam",
                field: "jobTeam" as keyof PRD3031_R01GridRowData,
                width: 75,
                editable: false,
                cellStyle: (params) => getCellStyle(params, "jobTeam"),
                cellRenderer: (params: any) => {
                    if (isSubtotalRow(params.data)) return "";
                    return jobTeamList.find((v) => v.minorCd === params.data?.jobTeam)?.codeNm || "";
                },
                valueGetter: (params: any) =>
                    jobTeamList.find((v) => v.minorCd === params.data?.jobTeam)?.codeNm || "",
            },
            {
                headerName: "품목코드",
                colId: "itemCd",
                field: "itemCd" as keyof PRD3031_R01GridRowData,
                width: 110,
                editable: false,
                cellStyle: (params) => getCellStyle(params, "itemCd"),
                cellRenderer: (params: any) => {
                    if (params.node.rowPinned === "bottom") return "";
                    return params.data?.itemCd || "";
                },
            },
            {
                headerName: "품명",
                colId: "itemNm",
                field: "itemNm" as keyof PRD3031_R01GridRowData,
                width: 200,
                editable: false,
                cellStyle: (params) => getCellStyle(params, "itemNm"),
                valueFormatter: (params: any) => params.data?.itemNm || "",
            },
            {
                headerName: "규격",
                colId: "itemSpec",
                field: "itemSpec" as keyof PRD3031_R01GridRowData,
                width: 200,
                editable: false,
                cellStyle: (params) => getCellStyle(params, "itemSpec"),
                valueFormatter: (params: any) => params.data?.itemSpec || "",
            },
            {
                headerName: "단위",
                colId: "itemUnit",
                field: "itemUnit" as keyof PRD3031_R01GridRowData,
                width: 80,
                editable: false,
                cellStyle: (params) => getCellStyle(params, "itemUnit"),
                cellRenderer: (params: any) => {
                    if (isSubtotalRow(params.data)) return "";
                    return codeMaps.unit.get(params.data?.itemUnit) || "";
                },
                valueGetter: (params: any) =>
                    isSubtotalRow(params.data) ? "" : (codeMaps.unit.get(params.data?.itemUnit) || ""),
                ...CommonCodeColumn.options(unitList, false, undefined, undefined),
            },
            {
                headerName: "지시량",
                colId: "orderQty",
                field: "orderQty" as keyof PRD3031_R01GridRowData,
                width: 75,
                editable: false,
                type: "numericColumn",
                headerClass: aggridHeaderClass,
                cellStyle: (params) => getCellStyle(params, "orderQty"),
                valueFormatter: (p: any) => formatNumber(p.value || 0),
                cellRenderer: (params: any) => {
                    if (isSubtotalRow(params.data)) return formatNumber(params.data.orderQty || 0);
                    return params.valueFormatted ?? (params.value ?? "");
                },
            },
            {
                headerName: "작업자",
                colId: "empNm",
                field: "empNm" as keyof PRD3031_R01GridRowData,
                width: 80,
                editable: false,
                cellStyle: (params) => getCellStyle(params, "empNm"),
                cellRenderer: (params: any) => {
                    if (isSubtotalRow(params.data)) return "";
                    if (params.node.rowPinned === "bottom") return "";
                    return codeMaps.emp.get(params.data?.productEmp) || "";
                },
                valueGetter: (params: any) =>
                    isSubtotalRow(params.data) ? "" : (codeMaps.emp.get(params.data?.productEmp) || ""),
            },
            {
                headerName: "생산량",
                colId: "productQty",
                field: "productQty" as keyof PRD3031_R01GridRowData,
                width: 65,
                editable: false,
                type: "numericColumn",
                headerClass: aggridHeaderClass,
                cellStyle: (params) => getCellStyle(params, "productQty"),
                valueFormatter: (p: any) => formatNumber(p.value || 0),
                cellRenderer: (params: any) => {
                    if (isSubtotalRow(params.data)) return formatNumber(params.data.productQty || 0);
                    return params.valueFormatted ?? (params.value ?? "");
                },
            },
            {
                headerName: "불량",
                colId: "badQty",
                field: "badQty" as keyof PRD3031_R01GridRowData,
                width: 80,
                editable: false,
                type: "numericColumn",
                headerClass: aggridHeaderClass,
                cellStyle: (params) => getCellStyle(params, "badQty"),
                valueFormatter: (p: any) => formatNumber(p.value || 0),
                cellRenderer: (params: any) => {
                    if (isSubtotalRow(params.data)) return formatNumber(params.data.badQty || 0);
                    return params.valueFormatted ?? (params.value ?? "");
                },
            },
            {
                headerName: "실적량",
                colId: "acceptQty",
                field: "acceptQty" as keyof PRD3031_R01GridRowData,
                width: 100,
                editable: false,
                type: "numericColumn",
                headerClass: aggridHeaderClass,
                cellStyle: (params) => getCellStyle(params, "acceptQty"),
                valueFormatter: (p: any) => formatNumber(p.value || 0),
                cellRenderer: (params: any) => {
                    if (isSubtotalRow(params.data)) return formatNumber(params.data.acceptQty || 0);
                    return params.valueFormatted ?? (params.value ?? "");
                },
            },
            {
                headerName: "불량률(%)",
                colId: "badRate",
                field: "badRate" as keyof PRD3031_R01GridRowData,
                width: 80,
                editable: false,
                type: "numericColumn",
                headerClass: aggridHeaderClass,
                cellStyle: (params: any) => {
                    const base = getCellStyle(params, "badRate") as any;
                    return { ...(base || {}), textAlign: "right" };
                },
                valueGetter: (params: any) => {
                    const data = params.data || {};
                    const productQty = Number(data.productQty) || 0;
                    const badQty = Number(data.badQty) || 0;
                    if (!isFinite(productQty) || productQty <= 0 || !isFinite(badQty) || badQty < 0) return 0;
                    return (badQty / productQty) * 100;
                },
                valueFormatter: (p: any) => `${(Number(p.value) || 0).toFixed(2)}`,
            },
        ],
        [
            hoveredRowIndex,
            gridRowData,
            selectedPRD3031_R01List,
            compCdList,
            ordTypeList,
            unitList,
            employeeCdList,
            workPlaceList,
            jobTeamList,
            codeMaps,
            getCellStyle,
        ],
    );

    const columnDefs: ColDef<PRD3031_R01GridRowData>[] = useMemo(() => {
        if (columnState === null) {
            return baseColumnDefs;
        }
        return GridCustomColumnDefs<PRD3031_R01GridRowData>({
            baseColumnDefs,
            columnState,
            headerNames,
            fallbackHeaderClass: aggridHeaderClass,
        });
    }, [columnState, settingRowIndex, baseColumnDefs, headerNames]);

    const defaultColumnState = useMemo(
        () => buildDefaultColumnStateFromDefs(baseColumnDefs, [])
    , [baseColumnDefs]);

    // 하단 합계 행 (소계 행 제외)
    const pinnedBottomRow = useMemo(() => {
        const detailRows = gridRowData.filter((row) => !isSubtotalRow(row));
        const totals = detailRows.reduce(
            (acc, item) => ({
                orderQty: acc.orderQty + (Number(item.orderQty) || 0),
                productQty: acc.productQty + (Number(item.productQty) || 0),
                badQty: acc.badQty + (Number(item.badQty) || 0),
                acceptQty: acc.acceptQty + (Number(item.acceptQty) || 0),
            }),
            { orderQty: 0, productQty: 0, badQty: 0, acceptQty: 0 },
        );

        return [
            {
                rowIndex: "",
                productResultNo: `${detailRows.length.toLocaleString()}건`,
                orderQty: totals.orderQty,
                productQty: totals.productQty,
                badQty: totals.badQty,
                acceptQty: totals.acceptQty,
                editable: false,
            },
        ];
    }, [gridRowData]);

    const getRowStyleForPRD3031 = useCallback((params: any) => {
        if (params.node.rowPinned === "bottom") {
            return { background: "#F0F0F0", fontWeight: "bold", borderTop: "none" };
        }
        return undefined;
    }, []);

    useEffect(() => { ensureSelectedRow(); }, [gridRowData, selectedPRD3031_R01List]);

    return (
        <div className="flex flex-col gap-3 h-full min-h-0">
            <GridContextWrapper<PRD3031_R01GridRowData>
            	enableCellCalculation
                gridRef={gridRef}
                paddedGridData={gridRowData}
                setPaddedGridData={undefined}
                initSelected={initSelectedPRD3031_R01List}
                defaultRowData={defaultProductResultData}
                baseColumnDefs={columnState ? columnDefs : baseColumnDefs}
                defaultColumnState={defaultColumnState}
                setColumnState={setColumnState}
                localStorageKey={COLUMN_STATE_KEY}
                enableDataHistory={false}
                menuItems={undefined}
            >
                <Grid_Touch<PRD3031_R01GridRowData>
                    ref={gridRef}
                    rowData={gridRowData}
                    columnDefs={columnDefs}
                    defaultColDef={defaultColDef}
                    getRowId={getRowIdForGrid}
                    onCellKeyDown={undefined}
                    rowClassRules={{
                        ...createRowClassRules<PRD3031_R01GridRowData>({
                            idKey: "productResultNo",
                            selectedList: selectedPRD3031_R01List,
                            deletedList: [],
                        }),
                        'subtotal-row': (params: any) => isItemSubtotalRow(params.data),
                        'date-subtotal-row': (params: any) => isDateSubtotalRow(params.data),
                    }}
                    onRowClicked={handleRowClick}
                    pinnedBottomRowData={pinnedBottomRow}
                    setHoveredRowIndex={setHoveredRowIndex}
                    columnStateKey={COLUMN_STATE_KEY}
                    setColumnState={setColumnState}
                    scrollTop={scrollTop}
                    setScrollTop={setScrollTop}
                    getRowStyle={getRowStyleForPRD3031}
                    isRowSelectable={(node) => !!node.data && !isSubtotalRow(node.data as PRD3031_R01GridRowData)}
                    suppressRowClickSelection={true}
                    ensureDomOrder={true}
                />
            </GridContextWrapper>
        </div>
    );
};
